import React, { useEffect, useRef } from 'react'
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image'
import { PrismicRichText } from '@prismicio/react'
import { RichTextField } from '@prismicio/types'

import CustomLink from 'src/utils/customLink'
import { PrismicLinkType } from 'src/typings/generated/graphql'
import * as GlobalStyles from 'src/styles/global.module.scss'
import * as Styles from './hoverCardGridModal.module.scss'
import type { HoverCardGridButtonVariant } from './hoverCardGrid'

export type HoverCardGridModalCard = {
  title?: string
  description?: RichTextField
  image?: IGatsbyImageData
  imageAlt?: string
  modalContent?: RichTextField
  modalButtonText?: string
  modalButtonVariant?: HoverCardGridButtonVariant
  modalButtonLink?: PrismicLinkType
}

type Props = {
  card: HoverCardGridModalCard | null
  isOpen: boolean
  isClosing?: boolean
  onClose: () => void
  onModalButtonClick?: () => void
  onExited?: () => void
}

const HoverCardGridModal = ({
  card,
  isOpen,
  isClosing = false,
  onClose,
  onModalButtonClick,
  onExited,
}: Props) => {
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return undefined

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    dialogRef.current?.focus()

    return () => {
      document.removeEventListener('keydown', handleKey)
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen, onClose])

  if (!isOpen || !card) return null

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose()
  }

  const handleAnimationEnd = (event: React.AnimationEvent<HTMLDivElement>) => {
    if (isClosing && event.target === event.currentTarget) onExited?.()
  }

  const richTextComponents = {
    hyperlink: ({ node, children }: any) =>
      CustomLink({ link: node.data, children }),
  }

  const hasRichTextContent = (field?: RichTextField): boolean => {
    if (!field || !Array.isArray(field) || field.length === 0) return false
    return field.some(
      (block: any) =>
        (typeof block.text === 'string' && block.text.trim().length > 0) ||
        block.type === 'image' ||
        block.type === 'embed'
    )
  }

  const showModalContent = hasRichTextContent(card.modalContent)
  const modalButtonOpensModal = card.modalButtonVariant === 'modal'
  const showModalButton = Boolean(
    card.modalButtonText?.trim() &&
      (!modalButtonOpensModal || onModalButtonClick)
  )

  return (
    <div
      className={Styles.overlay}
      data-state={isClosing ? 'closing' : 'open'}
      role="presentation"
      onClick={handleOverlayClick}
      onAnimationEnd={handleAnimationEnd}
    >
      <div
        ref={dialogRef}
        className={Styles.dialog}
        data-state={isClosing ? 'closing' : 'open'}
        role="dialog"
        aria-modal="true"
        aria-labelledby="hover-card-grid-modal-title"
        tabIndex={-1}
      >
        <button
          type="button"
          className={Styles.closeButton}
          onClick={onClose}
          aria-label="Close dialog"
        >
          ✕
        </button>
        {card.image && (
          <div className={Styles.imageWrapper}>
            <GatsbyImage
              className={Styles.image}
              image={card.image}
              alt={card.imageAlt ?? ''}
            />
          </div>
        )}
        {card.title && (
          <h2 id="hover-card-grid-modal-title" className={Styles.title}>
            {card.title}
          </h2>
        )}
        {card.description && (
          <div className={Styles.description}>
            <PrismicRichText
              field={card.description}
              components={richTextComponents}
            />
          </div>
        )}
        {showModalContent && (
          <div className={Styles.modalContent}>
            <PrismicRichText
              field={card.modalContent}
              components={richTextComponents}
            />
          </div>
        )}
        {showModalButton && (
          <div className={Styles.modalButtonWrapper}>
            {modalButtonOpensModal ? (
              <button
                type="button"
                className={`${GlobalStyles.redButton} ${Styles.modalButton}`}
                onClick={onModalButtonClick}
              >
                {card.modalButtonText}
              </button>
            ) : (
              <CustomLink
                link={card.modalButtonLink}
                className={`${GlobalStyles.redButton} ${Styles.modalButton}`}
              >
                {card.modalButtonText}
              </CustomLink>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default HoverCardGridModal
